// ALL_GRADING_V1
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';


const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function apiHeaders() {
  return {
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY,

    'Content-Type':
      'application/json'
  };
}


async function readJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
        ' failed (' +
        response.status +
        '): ' +
        (
          text ||
          response.statusText
        )
    );
  }

  return text
    ? JSON.parse(text)
    : null;
}


async function fetchAllGrading(
  session
) {
  if (!session?.cmsToken) {
    throw new Error(
      'Instructor session is unavailable.'
    );
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/' +
        'get_instructor_grading_queue',
      {
        method:
          'POST',

        headers:
          apiHeaders(),

        body:
          JSON.stringify({
            p_token:
              session.cmsToken
          })
      }
    );

  const result =
    await readJson(
      response,
      'Loading All Grading'
    );

  if (
    result?.ok !== true
  ) {
    throw new Error(
      result?.message ||
        'All Grading could not be loaded.'
    );
  }

  return Array.isArray(
    result.items
  )
    ? result.items
    : [];
}


export default function AllGrading({
  session,
  onOpenItem
}) {
  const [items, setItems] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');


  const refresh =
    useCallback(
      async () => {
        try {
          const rows =
            await fetchAllGrading(
              session
            );

          setItems(rows);
          setError('');

        } catch (err) {
          setError(
            err?.message ||
              'All Grading could not be loaded.'
          );

        } finally {
          setLoading(false);
        }
      },
      [session]
    );


  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);


  useEffect(() => {
    const handleFocus =
      () => refresh();

    const handleVisibility =
      () => {
        if (
          document
            .visibilityState ===
            'visible'
        ) {
          refresh();
        }
      };

    window.addEventListener(
      'focus',
      handleFocus
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {
      window.removeEventListener(
        'focus',
        handleFocus
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );
    };
  }, [refresh]);


  const groups =
    useMemo(
      () => {
        const byCourse =
          new Map();

        items.forEach(
          item => {
            const courseId =
              String(
                item?.courseId ||
                ''
              );

            if (!courseId) {
              return;
            }

            if (
              !byCourse.has(
                courseId
              )
            ) {
              byCourse.set(
                courseId,
                {
                  courseId,

                  courseName:
                    item.courseName ||
                    'Untitled Course',

                  semesterName:
                    item.semesterName ||
                    '',

                  items: []
                }
              );
            }

            byCourse
              .get(courseId)
              .items
              .push(item);
          }
        );

        return Array.from(
          byCourse.values()
        );
      },
      [items]
    );


  const total =
    items.reduce(
      (
        sum,
        item
      ) =>
        sum +
        Math.max(
          0,
          Number(
            item?.count
          ) || 0
        ),
      0
    );


  return (
    <div>
      <div
        style={
          styles.headingRow
        }
      >
        <div>
          <h2
            style={
              styles.heading
            }
          >
            All Grading
          </h2>

          <div
            style={
              styles.subheading
            }
          >
            Submitted work still awaiting grading.
          </div>
        </div>

        <div
          style={
            styles.headingActions
          }
        >
          <span
            style={
              styles.total
            }
          >
            {total}
          </span>

          <button
            type="button"
            style={
              styles.refreshButton
            }
            disabled={loading}
            onClick={refresh}
          >
            Refresh
          </button>
        </div>
      </div>


      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}


      {loading ? (
        <div style={styles.muted}>
          Loading grading...
        </div>

      ) : groups.length === 0 ? (
        <div style={styles.empty}>
          Nothing is waiting to be graded.
        </div>

      ) : (
        <div style={styles.groups}>
          {groups.map(
            group => (
              <section
                key={
                  group.courseId
                }
                style={
                  styles.group
                }
              >
                <div
                  style={
                    styles.courseHeader
                  }
                >
                  <strong>
                    {group.courseName}
                  </strong>

                  {group
                    .semesterName && (
                    <span
                      style={
                        styles.semester
                      }
                    >
                      {group
                        .semesterName}
                    </span>
                  )}
                </div>

                <div
                  style={
                    styles.rows
                  }
                >
                  {group.items.map(
                    item => (
                      <button
                        type="button"
                        key={
                          item.key
                        }
                        style={
                          styles.row
                        }
                        onClick={() =>
                          onOpenItem?.(
                            item
                          )
                        }
                      >
                        <span
                          style={
                            styles.label
                          }
                        >
                          {item.label}
                        </span>

                        <strong
                          style={
                            styles.count
                          }
                        >
                          {item.count}
                        </strong>
                      </button>
                    )
                  )}
                </div>
              </section>
            )
          )}
        </div>
      )}
    </div>
  );
}


const styles = {
  headingRow: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems:
      'flex-start',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '18px'
  },

  heading: {
    margin: 0,
    color: '#1f2933',
    fontSize: '20px'
  },

  subheading: {
    marginTop: '4px',
    color: '#66788a',
    fontSize: '13px'
  },

  headingActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },

  total: {
    minWidth: '34px',
    height: '34px',
    borderRadius: '17px',
    background: '#233746',
    color: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 10px',
    fontWeight: '800',
    fontSize: '13px'
  },

  refreshButton: {
    border:
      '1px solid #cbd5df',
    borderRadius: '7px',
    background: 'white',
    color: '#526575',
    padding: '7px 11px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  groups: {
    display: 'grid',
    gap: '16px'
  },

  group: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#fff'
  },

  courseHeader: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'baseline',
    gap: '12px',
    padding: '12px 14px',
    background: '#f7f9fa',
    borderBottom:
      '1px solid #e2e8ee',
    color: '#233746',
    flexWrap: 'wrap'
  },

  semester: {
    color: '#7a8995',
    fontSize: '12px',
    fontWeight: '600'
  },

  rows: {
    display: 'grid'
  },

  row: {
    width: '100%',
    border: 0,
    borderBottom:
      '1px solid #edf1f4',
    background: 'white',
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'space-between',
    gap: '14px',
    textAlign: 'left',
    color: '#263746',
    cursor: 'pointer'
  },

  label: {
    minWidth: 0,
    fontSize: '14px',
    fontWeight: '650'
  },

  count: {
    minWidth: '28px',
    textAlign: 'right',
    color: '#215b7d',
    fontSize: '15px'
  },

  error: {
    marginBottom: '14px',
    padding: '10px 12px',
    border:
      '1px solid #e8bcbc',
    borderRadius: '8px',
    background: '#faeaea',
    color: '#a12a2a',
    fontSize: '13px'
  },

  muted: {
    color: '#66788a',
    fontSize: '14px'
  },

  empty: {
    padding: '24px',
    border:
      '1px dashed #ccd6de',
    borderRadius: '9px',
    color: '#66788a',
    textAlign: 'center',
    fontSize: '14px'
  }
};
